/**
 * API del documento de Brecha de Género (C-155).
 *
 * El backend no guarda estado entre llamadas: las páginas del libro de familia
 * se quedan en el navegador y se vuelven a subir al generar.
 */

import axios from 'axios';
import { saveAs } from 'file-saver';

import supabaseClient from '../utils/supabaseInit';

// Para desarrollar contra el Django de WSL, comenta la primera y descomenta la
// segunda. Django tiene que arrancar con `runserver 0.0.0.0:8000`.
// const API_BASE = 'https://api.office.coventia.es';
const API_BASE = 'https://api.office.coventia.es';

const ENDPOINT_LEER_LIBRO = API_BASE + '/ai/leer_libro_familia';
const ENDPOINT_GENERAR = API_BASE + '/enviar-datos-brecha-genero/';
const MEDIA_GENERADOS = API_BASE + '/media/generated/';

export const TIPO_FORMULARIO = 'brecha_genero';

/** Workflow n8n "GENERADOR DOCUMENTOS Brecha Género - Consultar hubspot" (WcdFngddbFfDGCav). */
const WEBHOOK_NEGOCIO_HUBSPOT =
    'https://n8n-615087458109.europe-southwest1.run.app/webhook/brecha-genero-negocio';

/** Formatos aceptados por n8n; los PDF no valen. */
export const FORMATOS_LIBRO = ['.jpg', '.jpeg', '.png', '.tif', '.tiff', '.bmp', '.webp', '.gif'];

/** Token de Supabase sin prefijo "Bearer", como en el resto del office. */
const cabeceras = async () => {
    const { data } = await supabaseClient.auth.getSession();
    return {
        'Content-Type': 'multipart/form-data',
        'Authorization': data?.session?.access_token,
    };
};

export const formatoAceptado = (file) => (
    FORMATOS_LIBRO.some((ext) => file.name.toLowerCase().endsWith(ext))
);

/**
 * Resuelve un número de negocio en HubSpot vía n8n.
 * El mapeo de propiedades vive en el workflow; aquí no hay internal names.
 *
 * @param {string} numeroNegocio
 * @returns {Promise<{encontrado: boolean, dni_nie?: string, email?: string, telefono?: string}>}
 */
export const buscarNegocioEnHubspot = async (numeroNegocio) => {
    const response = await axios.get(WEBHOOK_NEGOCIO_HUBSPOT, {
        params: { numero_negocio: numeroNegocio },
        timeout: 30000,
    });
    return response.data || {};
};

/**
 * Envía las páginas del libro de familia a leer (Gemini vía n8n).
 *
 * @param {File[]} imagenes
 * @returns {Promise<object>} { solicitante, hijos, otro_progenitor, sin_clasificar, errores }
 */
export const leerLibroFamilia = async (imagenes) => {
    const formData = new FormData();
    imagenes.forEach((imagen) => formData.append('libro_familia', imagen, imagen.name));

    const response = await axios.post(ENDPOINT_LEER_LIBRO, formData, {
        headers: await cabeceras(),
        timeout: 180000, // Gemini: unos segundos por imagen
    });
    return response.data;
};

/**
 * Webhook de n8n que lee el DNI del solicitante con OCR.
 *
 * Es la variante de brecha de género: devuelve los apellidos SEPARADOS, que es
 * lo que pide el C-155, más la dirección del reverso.
 *
 * Workflow: "OCR DNI (Brecha género)" (sf8itUCtUdiwcUJU).
 */
const WEBHOOK_OCR_DNI =
    'https://n8n-615087458109.europe-southwest1.run.app/webhook/0f2e5d08-98f6-4568-b892-625b1d600e91';

/**
 * Lee el DNI del solicitante.
 *
 * Los campos se llaman `dni_front` / `dni_back` porque así los busca el
 * workflow. No confundir con `dni_frontal` / `dni_reverso`, que son los que
 * viajan al generador para anexarlos al PDF.
 *
 * @param {File} anverso
 * @param {File} [reverso] si falta, el workflow lee solo el anverso
 * @returns {Promise<object>} { encontrado, dni_nie, nombre, primer_apellido,
 *          segundo_apellido, direccion, codigo_postal, provincia }
 */
export const leerDni = async (anverso, reverso) => {
    const formData = new FormData();
    formData.append('dni_front', anverso, anverso.name);
    if (reverso) formData.append('dni_back', reverso, reverso.name);

    const response = await axios.post(WEBHOOK_OCR_DNI, formData, { timeout: 120000 });
    return response.data || {};
};

/**
 * Genera el C-155 con los datos revisados y las imágenes del libro.
 *
 * @param {FormData} formData
 * @returns {Promise<string[]>} nombres de los documentos generados
 */
export const generarDocumentos = async (formData) => {
    const response = await axios.post(ENDPOINT_GENERAR, formData, { headers: await cabeceras() });
    return response.data?.documentos_generados || [];
};

/**
 * Descarga los documentos de /media/generated/ y los devuelve para poder reenviarlos.
 *
 * @returns {Promise<Array<{nombre: string, blob: Blob}>>}
 */
export const descargarGenerados = async (nombres = []) => {
    const descargados = [];
    for (const nombre of nombres) {
        const response = await fetch(MEDIA_GENERADOS + nombre);
        const blob = await response.blob();
        saveAs(blob, nombre);
        descargados.push({ nombre, blob });
    }
    return descargados;
};

/** A firma van solo los PDF; el .docx es la copia editable del agente. */
export const esPdf = (nombre) => /\.pdf$/i.test(nombre || '');

/** Workflow n8n "Generador de documentos Brecha Genero - Enviar a firma". */
const WEBHOOK_ENVIAR_A_FIRMA =
    'https://n8n-615087458109.europe-southwest1.run.app/webhook/0a46b389-c49b-4289-9258-7f04fbfe87d1';

/**
 * Envía los PDF al workflow de firma.
 * Los campos van como `documento00`, `documento01`… porque n8n ordena
 * binarios alfabéticamente y `documento1/10/2` rompería el orden.
 *
 * @param {object} datos { idNegocio, nombreCliente, emailCliente, localidad }
 * @param {Array<{nombre: string, blob: Blob}>} documentos
 */
export const enviarAFirma = async ({ idNegocio, nombreCliente, emailCliente, localidad }, documentos = []) => {
    const formData = new FormData();
    formData.append('id_negocio', idNegocio);
    formData.append('nombre_cliente', nombreCliente || '');
    formData.append('email_cliente', emailCliente || '');
    // Es el campo "Lugar" del formulario, el del pie del impreso.
    formData.append('localidad', localidad || '');
    documentos.forEach(({ nombre, blob }, i) => {
        formData.append(`documento${String(i).padStart(2, '0')}`, blob, nombre);
    });

    await axios.post(WEBHOOK_ENVIAR_A_FIRMA, formData, { timeout: 60000 });
};
